import { BookOpen, Sparkles, Search, Wand2, Users, Gift, TreeDeciduous, Clock, IceCreamCone } from "lucide-react";
import { IMAGES } from "../data/site";
import { PageHero, Reveal, SectionHeading, CallButton, PartyButton } from "../components/Shared";

const features = [
  { icon: BookOpen, title: "Reading", text: "A cozy spot for children to open a book and slow down." },
  { icon: Sparkles, title: "Imagination", text: "A whimsical space where imaginations take over." },
  { icon: Search, title: "Discovery", text: "Little details waiting to be found on every visit." },
  { icon: Wand2, title: "Whimsical Decorations", text: "Storybook touches that make the space feel magical." },
  { icon: Users, title: "Family Time", text: "A place to sit together and simply enjoy the afternoon." },
  { icon: Gift, title: "Rewards", text: "Read by the tree for 20 minutes and earn a free small snowball." },
];

export default function ReadingTree() {
  return (
    <>
      <PageHero
        eyebrow="A signature spot"
        title="Discover the Reading Tree"
        sub="Sit beneath the Reading Tree, discover little doors and whimsical decorations, and let your imagination take over."
        testid="reading-hero"
        bg="bg-green-50"
      />

      <section className="px-4 py-20 sm:px-6" data-testid="reading-intro-section">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <img src={IMAGES.readingTree.url} alt={IMAGES.readingTree.alt} loading="lazy" className="aspect-[4/3] w-full rounded-3xl object-cover shadow-xl shadow-green-600/20" data-testid="reading-intro-image" />
          </Reveal>
          <Reveal delay={0.1}>
            <span className="inline-flex items-center gap-2 rounded-full bg-green-600 px-5 py-2 text-sm font-extrabold text-white shadow-lg shadow-green-600/30" data-testid="reading-rewards-badge">
              <TreeDeciduous className="h-4 w-4" aria-hidden="true" />
              Our very own Reading Tree w/ Rewards
            </span>
            <h2 className="mt-5 text-3xl font-semibold sm:text-4xl">Part of What Makes Ms. Jeanette's Different</h2>
            <p className="mt-4 text-base text-slate-600 md:text-lg">
              Anyone can serve a snowball. At Ms. Jeanette's, children can also sit beneath the Reading Tree,
              explore a world of little doors and whimsical decorations, and turn an ordinary afternoon into a
              storybook adventure — with rewards for readers, too.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-green-50 px-4 py-20 sm:px-6" data-testid="reading-features-section">
        <SectionHeading eyebrow="Under the tree" title="What You'll Find" testid="reading-features-heading" />
        <div className="mx-auto grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <Reveal key={feature.title} delay={i * 0.05}>
                <div data-testid={`reading-feature-${i}`} className="flex h-full items-start gap-4 rounded-3xl bg-white p-6 shadow-lg shadow-green-600/10 transition-transform duration-200 hover:-translate-y-1.5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-green-100">
                    <Icon className="h-6 w-6 text-green-600" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-semibold">{feature.title}</h3>
                    <p className="mt-1 text-sm font-semibold text-slate-600">{feature.text}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="px-4 py-20 sm:px-6" data-testid="reading-rewards-section">
        <SectionHeading eyebrow="Reading Tree w/ Rewards" title="How the Rewards Work" testid="reading-rewards-heading" />
        <Reveal className="mx-auto mb-12 max-w-3xl">
          <div className="rounded-3xl bg-green-600 px-6 py-5 text-center shadow-lg shadow-green-600/30" data-testid="reading-rewards-banner">
            <p className="font-display text-xl font-semibold text-white sm:text-2xl">
              Read a book by the tree for 20 minutes → earn a free small snowball
            </p>
          </div>
        </Reveal>
        <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-3">
          {[
            { icon: BookOpen, title: "1. Pick a Book", text: "Grab a book and find a cozy spot beneath the Reading Tree." },
            { icon: Clock, title: "2. Read for 20 Minutes", text: "Settle in, slow down, and read by the tree for 20 minutes." },
            { icon: IceCreamCone, title: "3. Earn a Snowball", text: "Show Ms. Jeanette and enjoy a free small snowball — on the house!" },
          ].map((step, i) => {
            const Icon = step.icon;
            return (
              <Reveal key={step.title} delay={i * 0.08}>
                <div data-testid={`reward-step-${i + 1}`} className="h-full rounded-3xl border-2 border-green-200 bg-white p-7 text-center shadow-lg shadow-green-600/10 transition-transform duration-200 hover:-translate-y-1.5">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
                    <Icon className="h-7 w-7 text-green-600" aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-display text-xl font-semibold">{step.title}</h3>
                  <p className="mt-2 text-sm font-semibold text-slate-600">{step.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="bg-blue-600 px-4 py-20 text-center sm:px-6" data-testid="reading-cta-section">
        <Reveal>
          <h2 className="text-3xl font-semibold text-white sm:text-4xl">Come Sit Beneath the Tree</h2>
          <p className="mx-auto mt-4 max-w-xl text-base font-semibold text-blue-100 md:text-lg">
            Grab a snowball, find a cozy spot, and let the adventure begin.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <CallButton testid="reading-cta-call-btn" light />
            <PartyButton testid="reading-cta-party-btn" />
          </div>
        </Reveal>
      </section>
    </>
  );
}
